// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PasswordRequirementValidator from "@guestsupply/App/Components/Common/PasswordRequirements";
import TextField from "@guestsupply/MobiusOverrides/TextField/TextField";
import ChangePasswordActions from "@guestsupply/Overrides/Widgets/ChangePassword/ChangePasswordActions";
import { resetPasswordStyles } from "@guestsupply/Overrides/Widgets/ResetPassword/ResetPassword";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ChangePasswordHeader from "@insite/content-library/Widgets/ChangePassword/ChangePasswordHeader";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden/Hidden";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    validatePassword,
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    pageTitle: getCurrentPage(state).fields.title,
});

export interface ChangePasswordStyles {
    actions?: HiddenProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    signOutWarningText?: TypographyPresentationProps;
    userInformationTitle?: TypographyProps;
    userInformationGridItem?: GridItemProps;
    showPasswordsGridItem?: GridItemProps;
    showPasswordsCheckbox?: CheckboxPresentationProps;
    changePasswordLink?: LinkPresentationProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    // XNG-Changes
    passwordCriteria?: TypographyProps;
    userInformationTitleGridItem?: TypographyProps;
    currentPasswordIcon?: IconPresentationProps;
    newPasswordIcon?: IconPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
}

export const changePasswordStyles: ChangePasswordStyles = {};

const styles = changePasswordStyles;

const ChangePasswordView = ({ accountSettings, pageTitle, validatePassword }: Props) => {
    const [password, setPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordErrorMessage, setNewPasswordErrorMessage] = useState<ReactNode>("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [showValidation, setShowValidation] = useState(false);
    const [error, setError] = useState(false);
    // Xng-Changes
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
        logOutUserAfterPasswordChange,
    } = accountSettings;

    // Xng-Changes: disabling save button when there are invalid requirements
    useEffect(() => {
        setError(
            !password ||
                !!newPasswordErrorMessage ||
                !newPassword ||
                !confirmNewPassword ||
                confirmNewPassword !== newPassword ||
                !newPassword ||
                !passwordMinimumLength ||
                !passwordRequiresDigit ||
                !passwordRequiresLowercase ||
                !passwordRequiresUppercase ||
                !passwordRequiresSpecialCharacter,
        );
    }, [
        password,
        newPassword,
        newPasswordErrorMessage,
        confirmNewPassword,
        newPassword,
        passwordMinimumLength,
        passwordRequiresDigit,
        passwordRequiresLowercase,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
    ]);

    const currentPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        setPassword(event.currentTarget.value);
    };

    const newPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const newPassword = event.currentTarget.value;

        validatePassword({
            password: newPassword,
            onComplete: errorMessage => {
                setNewPasswordErrorMessage(errorMessage);
            },
        });

        setNewPassword(newPassword);
    };

    const confirmNewPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        setConfirmNewPassword(event.currentTarget.value);
    };

    const confirmNewPasswordError =
        showValidation && newPassword && confirmNewPassword && newPassword !== confirmNewPassword
            ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
            : "";

    return (
        <form>
            {/* Xng-Changes: comment out ootb code - following classic design */}
            {/* <ChangePasswordHeader
                title={pageTitle}
                password={password}
                newPassword={newPassword}
                confirmNewPassword={confirmNewPassword}
                error={error}
                showValidation={showValidation}
                setShowValidation={setShowValidation}
            ></ChangePasswordHeader> */}
            {logOutUserAfterPasswordChange && (
                <Typography {...styles.signOutWarningText}>
                    {siteMessage("Account_Automatic_SignOut_Warning")}
                </Typography>
            )}
            {/* <Typography {...styles.userInformationTitle}>{translate("Password Requirements")}</Typography>
            <GridContainer {...styles.passwordRequirementsGridContainer}>
                <GridItem {...styles.passwordRequirementsGridItem}>
                    {translate("Password must be at least {0} characters long", passwordMinimumLength.toString())}
                </GridItem>
                {passwordRequiresDigit && (
                    <GridItem {...styles.passwordRequirementsGridItem}>
                        {translate("Password must include at least one number")}
                    </GridItem>
                )}
                {passwordRequiresLowercase && (
                    <GridItem {...styles.passwordRequirementsGridItem}>
                        {translate("Password must include at least one lowercase character")}
                    </GridItem>
                )}
                {passwordRequiresUppercase && (
                    <GridItem {...styles.passwordRequirementsGridItem}>
                        {translate("Password must include at least one uppercase character")}
                    </GridItem>
                )}
                {passwordRequiresSpecialCharacter && (
                    <GridItem {...styles.passwordRequirementsGridItem}>
                        {translate("Password must include at least one non alphanumeric character")}
                    </GridItem>
                )}
            </GridContainer> */}

            {/* XNG-Changes: adding "Passwords are Case Sensitive" - following classic design */}

            <GridContainer>
                <GridItem {...resetPasswordStyles.passwordGridItem}>
                    <Typography {...styles.passwordCriteria}>{translate("Passwords are Case Sensitive")}</Typography>

                    <GridContainer {...resetPasswordStyles.passwordGridContainer}>
                        {/* XNG-Changes: adding "Enter your new password." - following classic design */}
                        <GridItem {...styles.userInformationGridItem}>
                            <Typography {...styles.userInformationTitleGridItem}>
                                {translate("Enter your new password.")}
                            </Typography>
                        </GridItem>
                        <GridItem {...resetPasswordStyles.newPasswordGridItem}>
                            <TextField
                                type={showCurrentPassword ? "text" : "password"}
                                label={translate("Current Password")}
                                onInput={currentPasswordChangeHandler}
                                autoComplete="current-password"
                                // Xng-Changes: placeholder
                                placeholder={translate("Current Password")}
                                iconProps={{ ...styles.currentPasswordIcon, src: showCurrentPassword ? EyeOff : Eye }}
                                showError={false}
                                iconClickableProps={{
                                    onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                        e.preventDefault();
                                        setShowCurrentPassword(!showCurrentPassword);
                                    },
                                }}
                            />
                        </GridItem>
                        <GridItem {...resetPasswordStyles.newPasswordGridItem}>
                            <TextField
                                type={showNewPassword ? "text" : "password"}
                                label={translate("New Password")}
                                onInput={newPasswordChangeHandler}
                                error={showValidation && newPasswordErrorMessage}
                                autoComplete="new-password"
                                // Xng-Changes: placeholder
                                showError={false}
                                placeholder={translate("New Password")}
                                iconProps={{ ...styles.newPasswordIcon, src: showNewPassword ? EyeOff : Eye }}
                                iconClickableProps={{
                                    onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                        e.preventDefault();
                                        setShowNewPassword(!showNewPassword);
                                    },
                                }}
                            />
                        </GridItem>
                        <GridItem {...resetPasswordStyles.newPasswordGridItem}>
                            <TextField
                                type={showConfirmPassword ? "text" : "password"}
                                label={translate("Confirm New Password")}
                                onInput={confirmNewPasswordChangeHandler}
                                error={confirmNewPasswordError}
                                autoComplete="new-password"
                                // Xng-Changes: placeholder
                                showError={false}
                                placeholder={translate("Confirm New Password")}
                                iconProps={{
                                    ...styles.confirmPasswordIcon,
                                    src: showConfirmPassword ? EyeOff : Eye,
                                }}
                                iconClickableProps={{
                                    onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                        e.preventDefault();
                                        setShowConfirmPassword(!showConfirmPassword);
                                    },
                                }}
                            />
                        </GridItem>
                        <GridItem {...resetPasswordStyles.newPasswordGridItem}>
                            <ChangePasswordActions
                                password={password}
                                newPassword={newPassword}
                                confirmNewPassword={confirmNewPassword}
                                error={error}
                                showValidation={showValidation}
                                setShowValidation={setShowValidation}
                                currentPassword={password}
                            ></ChangePasswordActions>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...resetPasswordStyles.requirementsGridItem}>
                    <Typography {...resetPasswordStyles.requirementsTitle}>
                        {translate("Password Requirements")}
                    </Typography>
                    <PasswordRequirementValidator
                        newPassword={newPassword}
                        minLength={passwordMinimumLength}
                        checkDigits={passwordRequiresDigit}
                        checkLowerCase={passwordRequiresLowercase}
                        checkUpperCase={passwordRequiresUppercase}
                        checkNonAlphaNumeric={passwordRequiresSpecialCharacter}
                        passwordsMatching={confirmNewPassword === newPassword}
                    />
                </GridItem>
            </GridContainer>
        </form>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ChangePasswordView),
    definition: {
        allowedContexts: ["ChangePasswordPage"],
        group: "Change Password",
    },
};

export default widgetModule;
